import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { StatCardData } from '../types';

const colorClasses: Record<StatCardData['color'], { bg: string; icon: string; border: string }> = {
  green: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-100' },
  blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-100' },
  purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600', border: 'border-purple-100' },
  orange: { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-100' },
  red: { bg: 'bg-red-50', icon: 'bg-red-100 text-red-600', border: 'border-red-100' },
  teal: { bg: 'bg-teal-50', icon: 'bg-teal-100 text-teal-600', border: 'border-teal-100' },
};

interface StatCardProps {
  data: StatCardData;
}

const StatCard: React.FC<StatCardProps> = ({ data }) => {
  const navigate = useNavigate();
  const colors = colorClasses[data.color];
  const isClickable = !!data.link;

  const handleClick = () => {
    if (data.link) navigate(data.link);
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white rounded-xl border ${colors.border} p-5 hover:shadow-md transition-all duration-200 ${
        isClickable ? 'cursor-pointer hover:scale-[1.02] active:scale-[0.98]' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg ${colors.icon} flex items-center justify-center text-lg`}>
          {data.icon}
        </div>
        {data.change ? (
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
              data.changeType === 'up'
                ? 'bg-blue-50 text-blue-600'
                : data.changeType === 'down'
                ? 'bg-red-50 text-red-600'
                : 'bg-gray-50 text-gray-500'
            }`}
          >
            {data.changeType === 'up' ? '↑' : data.changeType === 'down' ? '↓' : '→'}
            {data.change}
          </span>
        ) : isClickable ? (
          <span className="text-gray-300 text-sm">→</span>
        ) : null}
      </div>
      <p className="text-2xl font-bold text-gray-900 mb-1">{data.value}</p>
      <p className="text-sm text-gray-500">{data.label}</p>
      {data.subtitle && (
        <p className="text-xs text-gray-400 mt-0.5">{data.subtitle}</p>
      )}
    </div>
  );
};

export default StatCard;
